import type {
  Field,
  ShopHistoricTable,
} from "@okeys/shared-utils"
import {
  CalendarCheck,
  CalendarX,
  Clock,
  CreditCard,
  Hash,
  History,
  Image,
  Package,
  Palette,
  Settings,
  ShoppingBag,
  UserMinus,
  UserPlus,
  Users,
  Wrench,
} from "lucide-react"

// Fonction pour obtenir l'icône appropriée selon le type d'action
const getIconForActionType = (
  type: string,
  fieldsUpdates?: Field[]
): React.ComponentType<{ className?: string }> => {
  if (!type) return History

  switch (type) {
    case "PRODUCT_CREATED":
    case "PRODUCT_UPDATED":
    case "PRODUCT_DELETED":
    case "UNAVAILABILITY_PRODUCT_CREATED":
    case "UNAVAILABILITY_PRODUCT_DELETED":
      return Package

    case "SERVICE_CREATED":
    case "SERVICE_UPDATED":
    case "SERVICE_DELETED":
    case "UNAVAILABILITY_SERVICE_CREATED":
    case "UNAVAILABILITY_SERVICE_DELETED":
      return Wrench

    case "PRESTATERE_CREATED":
    case "PRESTATERE_UPDATED":
    case "PRESTATERE_DELETED":
    case "UNAVAILABILITY_PRESTATERE_CREATED":
    case "UNAVAILABILITY_PRESTATERE_DELETED":
      return Users

    case "SHOP_DATA_UPDATED":
      if (fieldsUpdates && fieldsUpdates.length > 0) {
        switch (fieldsUpdates[0]) {
          case "fidelityNumber":
            return CreditCard
          case "urlsImage-replace":
            return Image
          case "urlsImage-add":
            return Image
          case "urlsImage-delete":
            return Image
          case "urlLogoShop":
            return Image
          case "schedule":
            return Clock
          case "hashtag":
            return Hash
          case "theme":
            return Palette
        }
      }
      return Settings
    case "UNAVAILABILITY_SHOP_CREATED":
    case "UNAVAILABILITY_SHOP_DELETED":
    case "PROMOTION_CREATED":
    case "PROMOTION_UPDATED":
    case "PROMOTION_DELETED":
    case "CASH_REGISTER_CREATED":
    case "CASH_REGISTER_UPDATED":
    case "CASH_REGISTER_DELETED":
      return ShoppingBag

    case "CATEGORY_ORDER_UPDATED":
      return Settings

    default:
      return History
  }
}

// Fonction pour obtenir la couleur selon le type d'action
const getColorForActionType = (type: string): string => {
  if (!type) return "gray"

  switch (type) {
    case "PRODUCT_CREATED":
    case "SERVICE_CREATED":
    case "PRESTATERE_CREATED":
    case "PROMOTION_CREATED":
    case "CASH_REGISTER_CREATED":
    case "UNAVAILABILITY_SERVICE_DELETED":
    case "UNAVAILABILITY_PRODUCT_DELETED":
    case "UNAVAILABILITY_SHOP_DELETED":
    case "UNAVAILABILITY_PRESTATERE_DELETED":
      return "green"

    case "PRODUCT_DELETED":
    case "SERVICE_DELETED":
    case "PRESTATERE_DELETED":
    case "PROMOTION_DELETED":
    case "CASH_REGISTER_DELETED":
    case "UNAVAILABILITY_SHOP_CREATED":
      return "red"

    case "PRODUCT_UPDATED":
    case "SERVICE_UPDATED":
    case "PRESTATERE_UPDATED":
    case "PROMOTION_UPDATED":
    case "CASH_REGISTER_UPDATED":
    case "SHOP_DATA_UPDATED":
    case "CATEGORY_ORDER_UPDATED":
      return "blue"

    case "UNAVAILABILITY_SERVICE_CREATED":
    case "UNAVAILABILITY_PRODUCT_CREATED":
    case "UNAVAILABILITY_PRESTATERE_CREATED":
      return "orange"

    default:
      return "gray"
  }
}

// Fonction pour créer un label lisible à partir du type d'action
const createReadableLabel = (item: ShopHistoricTable): string => {
  const type = item.historicType
  if (!type) return "Action inconnue"

  switch (type) {
    case "PRODUCT_CREATED":
      return "Produit créé"
    case "PRODUCT_UPDATED":
      return "Produit modifié"
    case "PRODUCT_DELETED":
      return "Produit supprimé"
    case "SERVICE_CREATED":
      return "Service créé"
    case "SERVICE_UPDATED":
      return "Service modifié"
    case "SERVICE_DELETED":
      return "Service supprimé"
    case "SHOP_DATA_UPDATED":
      if (item.fieldsUpdates && item.fieldsUpdates.length > 0) {
        switch (item.fieldsUpdates[0]) {
          case "fidelityNumber":
            return "Programme de fidélité modifié"
          case "urlsImage-replace":
            return "Images remplacées"
          case "urlsImage-add":
            return "Images ajoutées"
          case "urlsImage-delete":
            return "Images supprimées"
          case "urlLogoShop":
            return "Logo de la boutique modifié"
          case "schedule":
            return "Horaires modifiés"
          case "hashtag":
            return "Hashtags modifiés"
          case "theme":
            return "Thème modifié"
          case "withBooking":
            return "Système de réservation modifié"
        }
      }
      return "Données de la boutique modifiées"
    case "PRESTATERE_CREATED":
      return `Prestataire ajouté ${
        "email" in item && item.email ? `( ${item.email} )` : ""
      }`
    case "NAME_CHANGED":
      return `Nom modifié de ${
        "oldName" in item && item.oldName ? `${item.oldName} ` : ""
      } à ${
        "newName" in item && item.newName ? `${item.newName}` : ""
      } ${"email" in item && item.email ? `( ${item.email} )` : ""}`
    case "PRESTATERE_DELETED":
      return `Prestataire supprimé ${
        "email" in item && item.email ? `( ${item.email} )` : ""
      }`
    case "PROMOTION_CREATED":
      return "Promotion créée"
    case "PROMOTION_UPDATED":
      return "Promotion modifiée"
    case "PROMOTION_DELETED":
      return "Promotion supprimée"
    case "CASH_REGISTER_CREATED":
      return "Caisse ajoutée"
    case "CASH_REGISTER_UPDATED":
      return "Caisse modifiée"
    case "CASH_REGISTER_DELETED":
      return "Caisse supprimée"
    case "UNAVAILABILITY_SERVICE_CREATED":
      return "Service indisponible"
    case "UNAVAILABILITY_SERVICE_DELETED":
      return "Service redisponible"
    case "UNAVAILABILITY_PRODUCT_CREATED":
      return "Produit indisponible"
    case "UNAVAILABILITY_PRODUCT_DELETED":
      return "Produit redisponible"
    case "UNAVAILABILITY_SHOP_CREATED":
      return "Boutique fermée"
    case "UNAVAILABILITY_SHOP_DELETED":
      return "Boutique ouverte"
    case "UNAVAILABILITY_PRESTATERE_CREATED":
      return "Prestataire indisponible"
    case "UNAVAILABILITY_PRESTATERE_DELETED":
      return "Prestataire redisponible"
    case "CATEGORY_ORDER_UPDATED":
      return "Ordre des catégories"

    default:
      return "modification"
  }
}

// Mapping des types d'actions pour l'affichage - maintenant basé sur les données du backend
export const getActionTypeInfo = (
  item: ShopHistoricTable
): {
  label: string
  color: string
  icon: React.ComponentType<{ className?: string }>
} => {
  // Vérification de sécurité
  if (!item) {
    return {
      label: "Action inconnue",
      color: "gray",
      icon: History,
    }
  }

  return {
    label: createReadableLabel(item),
    color: getColorForActionType(item.historicType),
    icon: getIconForActionType(item.historicType),
  }
}

export const formatDate = (timestamp: number) => {
  if (!timestamp) {
    return {
      date: "-",
      time: "-",
    }
  }

  const date = new Date(timestamp)
  return {
    date: date.toLocaleDateString("fr-FR"),
    time: date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  }
}

export const getBadgeVariant = (color: string) => {
  switch (color) {
    case "green":
      return "default"
    case "blue":
      return "secondary"
    case "red":
      return "destructive"
    case "orange":
      return "outline"
    case "purple":
      return "secondary"
    default:
      return "outline"
  }
}

// Fonction pour obtenir l'icône appropriée selon le type d'action pour les prestataires
const getIconForActionTypePrestatere = (
  type: string
): React.ComponentType<{ className?: string }> => {
  if (!type) return History

  switch (type) {
    case "RESERVATION_CREATED":
    case "RESERVATION_UPDATED":
      return CalendarCheck
    case "RESERVATION_CANCELLED":
    case "RESERVATION_ABSENT":
      return CalendarX
    case "RESERVATION_COMPLETED":
      return CalendarCheck
    case "SUBSCRIPTION_STARTED":
      return UserPlus
    case "SUBSCRIPTION_CANCELLED":
      return UserMinus
    case "KEYS_EARNED":
    case "KEYS_SPENT":
      return CreditCard
    default:
      return History
  }
}

// Fonction pour obtenir la couleur selon le type d'action pour les prestataires
const getColorForActionTypePrestatere = (type: string): string => {
  if (!type) return "gray"

  switch (type) {
    case "RESERVATION_CREATED":
    case "RESERVATION_COMPLETED":
    case "SUBSCRIPTION_STARTED":
    case "KEYS_EARNED":
      return "green"
    case "RESERVATION_CANCELLED":
    case "RESERVATION_ABSENT":
    case "SUBSCRIPTION_CANCELLED":
    case "KEYS_SPENT":
      return "red"
    case "RESERVATION_UPDATED":
      return "blue"
    default:
      return "gray"
  }
}

// Fonction pour créer un label lisible à partir du type d'action pour les prestataires
const createReadableLabelPrestatere = (item: ShopHistoricTable): string => {
  const type = item.historicType
  if (!type) return "Action inconnue"

  // This function is now only for ShopHistoricTable which uses HistoricType
  // The RESERVATION_* types are now handled separately in the interaction function
  return createReadableLabel(item)
}

// Fonction pour obtenir les informations d'affichage des actions pour les prestataires
export const getActionTypeInfoPrestatere = (
  item: ShopHistoricTable
): {
  label: string
  color: string
  icon: React.ComponentType<{ className?: string }>
} => {
  // Vérification de sécurité
  if (!item) {
    return {
      label: "Action inconnue",
      color: "gray",
      icon: History,
    }
  }

  return {
    label: createReadableLabelPrestatere(item),
    color: getColorForActionTypePrestatere(item.historicType),
    icon: getIconForActionTypePrestatere(item.historicType),
  }
}

// Fonction pour obtenir les informations d'affichage des actions pour les interactions
export const getActionTypeInfoInteraction = (
  item: { actionType: string }
): {
  label: string
  color: string
  icon: React.ComponentType<{ className?: string }>
} => {
  // Vérification de sécurité
  if (!item) {
    return {
      label: "Action inconnue",
      color: "gray",
      icon: History,
    }
  }

  const type = item.actionType
  if (!type) return { label: "Action inconnue", color: "gray", icon: History }

  let label = "Action inconnue"
  switch (type) {
    case "RESERVATION_CREATED":
      label = "Réservation créée"
      break
    case "RESERVATION_CANCELLED":
      label = "Réservation annulée"
      break
    case "RESERVATION_COMPLETED":
      label = "Réservation terminée"
      break
    case "RESERVATION_ABSENT":
      label = "Client absent"
      break
    case "RESERVATION_UPDATED":
      label = "Réservation modifiée"
      break
    case "SUBSCRIPTION_STARTED":
      label = "Abonnement démarré"
      break
    case "SUBSCRIPTION_CANCELLED":
      label = "Abonnement annulé"
      break
    case "KEYS_EARNED":
      label = "Clés gagnées"
      break
    case "KEYS_SPENT":
      label = "Clés dépensées"
      break
    default:
      label = "Action non reconnue"
  }

  return {
    label,
    color: getColorForActionTypePrestatere(type),
    icon: getIconForActionTypePrestatere(type),
  }
}
